import { create } from 'zustand';

import type { ActiveSuppressionRecord } from '../types';

/** Shared fallback for devices with no entry: a fresh `[]` per call would loop useSyncExternalStore. */
export const EMPTY_ACTIVE_SUPPRESSIONS: ActiveSuppressionRecord[] = [];
Object.freeze(EMPTY_ACTIVE_SUPPRESSIONS);

type ActiveSuppressionsState = {
  byDevice: Record<string, ActiveSuppressionRecord[]>;
  setDeviceActiveSuppressions: (deviceId: string, records: ActiveSuppressionRecord[]) => void;
  removeDevices: (deviceIds: string[]) => void;
  clearAll: () => void;
};

/**
 * Every mounted socket hook re-subscribes on reconnect, so the same subscribe snapshot arrives once
 * per mount as separately deserialized arrays. Equal content must not re-render every card.
 *
 * Whole records are compared: the backend pushes an upsert that rewrote only the body (frequency,
 * kind) under the same id and expiry, and a key-field comparison would drop it.
 */
const haveSameRecords = (
  current: ActiveSuppressionRecord[] | undefined,
  incoming: ActiveSuppressionRecord[],
): boolean => current !== undefined && JSON.stringify(current) === JSON.stringify(incoming);

export const useActiveSuppressionsStore = create<ActiveSuppressionsState>((set) => ({
  byDevice: {},
  setDeviceActiveSuppressions: (deviceId, records) =>
    set((state) => {
      // One socket frame reaches every mounted handler with the same deserialized array.
      if (state.byDevice[deviceId] === records) {
        return state;
      }

      if (haveSameRecords(state.byDevice[deviceId], records)) {
        return state;
      }

      if (records.length === 0) {
        if (!(deviceId in state.byDevice)) {
          return state;
        }

        const byDevice = { ...state.byDevice };
        delete byDevice[deviceId];
        return { byDevice };
      }

      return { byDevice: { ...state.byDevice, [deviceId]: records } };
    }),
  removeDevices: (deviceIds) =>
    set((state) => {
      const presentDeviceIds = deviceIds.filter((deviceId) => deviceId in state.byDevice);

      if (presentDeviceIds.length === 0) {
        return state;
      }

      const byDevice = { ...state.byDevice };
      for (const deviceId of presentDeviceIds) {
        delete byDevice[deviceId];
      }

      return { byDevice };
    }),
  clearAll: () =>
    set((state) => (Object.keys(state.byDevice).length === 0 ? state : { byDevice: {} })),
}));
